import {
  ActionAgentReportResponseDTO,
  GeneralInvestmentReportDTO,
  GeneralInvestmentRequestDTO,
  ReqIncentiveDetailsDTO,
  ReqIncentiveDetailsDTOResponse,
} from './../models/control-panel.model';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CommonDataDTO } from '@app/requests/model/combos-model';
import moment from 'moment';
import {
  AgentStatsRequestDTO,
  AgentStatsReportDTO,
  InvestDetailOperationRequestDTO,
  InvestmentByProductsDTO,
  InvestProductsDTO,
  OrderType,
  AgentType,
  OperationValidationRequest,
  OperationValidationResponse,
  AgentActionPlanDTO,
  OperationDetailRequestDTO,
  ReqOpsDetailsDTO,
} from '../models/control-panel.model';
import { ControlPanelServiceApi } from './control-panel-api.service';
import { NO_DATA_FOUND } from '@app/@core/constants/error.const';

@Injectable()
export class ControlPanelStore {
  public actionAgentTableColumn: string[] = [
    'actionAgent.investment',
    '',
    'actionAgent.numOperations',
    '',
    'actionAgent.percentageCO',
    '',
    'actionAgent.percentagePPT',
    '',
    'actionAgent.percentagePPB',
    '',
    'actionAgent.percentagePPP',
    '',
    'actionAgent.tirMedia',
    '',
    'actionAgent.totalCommission',
    '',
    'actionAgent.safeCommission',
    '',
    'actionAgent.feeCommission',
    '',
    'actionAgent.useInfo',
    '',
  ];

  public agentType: CommonDataDTO[] = [
    { id: AgentType.Group, value: 'investForm.group' },
    { id: AgentType.NifCif, value: 'investForm.nifcif' },
    { id: AgentType.Agent, value: 'investForm.agent' },
  ];
  public agentDetail: CommonDataDTO[] = [
    { id: OrderType.NifCif, value: 'NIF/CIF' },
    { id: OrderType.Agents, value: 'Agente' },
  ];

  constructor(private controlPanelApi: ControlPanelServiceApi) {}

  public getListProducts(filter?: any): Promise<CommonDataDTO[]> {
    return new Promise<any[]>((resolve, reject) => {
      this.controlPanelApi.getProducts(filter ? filter : {}).subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }

  public getListArticles(filter?: any): Promise<CommonDataDTO[]> {
    return new Promise<any[]>((resolve, reject) => {
      this.controlPanelApi.getArticles(filter ? filter : {}).subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }

  public getExternalCommercialsByFilter(filter?: any): Promise<CommonDataDTO[]> {
    return new Promise<any[]>((resolve, reject) => {
      this.controlPanelApi.getExternalCommercialsByFilter(filter ? filter : {}).subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }

  public getOperationsValidation(request: OperationValidationRequest): Promise<OperationValidationResponse> {
    return new Promise<OperationValidationResponse>((resolve, reject) => {
      this.controlPanelApi.getOperationValidation(request).subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }

  public getDocumentStatus(): Promise<CommonDataDTO[]> {
    return new Promise<any[]>((resolve, reject) => {
      this.controlPanelApi.getDocumentStatus().subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }

  public getInvestmentDetailReport(form: FormGroup): Promise<any> {
    const parameter: InvestDetailOperationRequestDTO = {
      dateFrom: form.get('initialDate')?.value.startOf('month').format('YYYY-MM-DD'),
      dateTo: form.get('finalDate')?.value.endOf('month').format('YYYY-MM-DD'),
      divisionId: form.get('division')?.value,
      groupId: form.get('typeAgent').value === AgentType.Group ? form.get('agent')?.value : null,
      prescriberId: form.get('typeAgent').value === AgentType.NifCif ? form.get('agent')?.value : null,
      establishmentId: form.get('typeAgent').value === AgentType.Agent ? form.get('agent')?.value : null,
      orderType: form.get('detail').value,
    };
    return new Promise<any[]>((resolve, reject) => {
      this.controlPanelApi.getInvestDetail(parameter).subscribe(
        (data) => {
          data ? resolve(data) : reject(data);
        },
        (error) => reject(error)
      );
    });
  }

  public getInvestorProductReport(form: FormGroup): Promise<InvestmentByProductsDTO> {
    const parameter: InvestProductsDTO = {
      dateFrom: form.get('initialDate')?.value.startOf('month').format('YYYY-MM-DD'),
      dateTo: form.get('finalDate')?.value.endOf('month').format('YYYY-MM-DD'),
      divisionId: form.get('division')?.value,
      groupId: form.get('typeAgent').value === AgentType.Group ? form.get('agent')?.value : null,
      prescriberId: form.get('typeAgent').value === AgentType.NifCif ? form.get('agent')?.value : null,
      establishmentId: form.get('typeAgent').value === AgentType.Agent ? form.get('agent')?.value : null,
    };

    return new Promise<InvestmentByProductsDTO>((resolve, reject) => {
      this.controlPanelApi.getProductInvestment(parameter).subscribe(
        (data) => resolve(data),
        (error) => reject(error)
      );
    });
  }

  public getAgentStatsReport(form: FormGroup): Promise<AgentStatsReportDTO> {
    const parameter: AgentStatsRequestDTO = {
      fromDate: form.get('initialDate')?.value.startOf('month').format('YYYY-MM-DD'),
      toDate: form.get('finalDate')?.value.endOf('month').format('YYYY-MM-DD'),
      divisionId: form.get('division')?.value,
      officeId: form.get('office')?.value,
      productId: form.get('product')?.value,
      articleId: form.get('typeProperty')?.value,
      chainId: form.get('macroChain')?.value,
    };

    return new Promise<AgentStatsReportDTO>((resolve, reject) => {
      this.controlPanelApi.getAgentStatistics(parameter).subscribe(
        (data) => resolve(data),
        (error) => reject(error)
      );
    });
  }

  public getActionAgentReport(request: AgentActionPlanDTO): Promise<ActionAgentReportResponseDTO> {
    return new Promise<ActionAgentReportResponseDTO>((resolve, reject) => {
      this.controlPanelApi.getActionAgentPlan(request).subscribe(
        (data) => (data ? resolve(data) : reject(new Error(NO_DATA_FOUND))),
        (error) => reject(new Error(error))
      );
    });
  }

  getGeneralInvestment(request: GeneralInvestmentRequestDTO): Promise<GeneralInvestmentReportDTO> {
    return new Promise<GeneralInvestmentReportDTO>((resolve, reject) => {
      this.controlPanelApi.getGeneralInvestment(request).subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }

  public getListExternalCommercialByFilter(parameter?: any): Promise<CommonDataDTO[]> {
    return new Promise<CommonDataDTO[]>((resolve, reject) => {
      this.controlPanelApi.getExternalCommercialByFilter(parameter).subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }

  public getListStatus(): Promise<CommonDataDTO[]> {
    return new Promise<CommonDataDTO[]>((resolve, reject) => {
      this.controlPanelApi.getStatusList().subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }

  public getListOrigins(): Promise<CommonDataDTO[]> {
    return new Promise<CommonDataDTO[]>((resolve, reject) => {
      this.controlPanelApi.getOrigins().subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }

  public getOperationDetailReport(form: FormGroup): Promise<ReqOpsDetailsDTO[]> {
    const request: OperationDetailRequestDTO = {
      divisionId: form.controls.division?.value,
      officeId: form.controls.office?.value,
      reqStatusId: form.controls.status?.value,
      groupId: form.controls.group?.value,
      agentId: form.controls.agent?.value,
      extCommercialId: form.controls.externalCommercial?.value,
      origin: form.controls.origin?.value,
      fromDate: moment(form.controls.startDate?.value).startOf('month').format('YYYY-MM-DD'),
      toDate: moment(form.controls.endDate?.value).endOf('month').format('YYYY-MM-DD'),
    };
    return new Promise<ReqOpsDetailsDTO[]>((resolve, reject) => {
      this.controlPanelApi.getOperationDetail(request).subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }

  getPenaltyDetailReport(form: FormGroup, page: number, size: number): Promise<ReqIncentiveDetailsDTOResponse> {
    const parameter: ReqIncentiveDetailsDTO = {
      establishmentId: form.get('establishment')?.value,
      dateFrom: form.get('initialDate')?.value.startOf('month').format('YYYY-MM-DD'),
      dateTo: form.get('finalDate')?.value.endOf('month').format('YYYY-MM-DD'),
      pageIndex: page,
      pageSize: size,
    };
    return new Promise<ReqIncentiveDetailsDTOResponse>((resolve, reject) => {
      this.controlPanelApi.getPenaltyDetail(parameter).subscribe(
        (data) => (data ? resolve(data) : reject(data)),
        (error) => reject(error)
      );
    });
  }
}
